/**
 * Consultation — v7.4 P6
 *
 * Workflow intégré:
 *   1. Sélectionner patient
 *   2. Taper les symptômes (debounce 600ms → IA suggère ICD-10 inline)
 *   3. Sélectionner un code → passe à l'étape prescription
 *   4. L'IA vérifie les interactions drug-drug
 *   5. SOAP notes générées
 *   6. Validation + audit
 *
 * L'IA disparaît dans le workflow — plus de page AI séparée à ouvrir.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, useMemo, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  Stethoscope, AlertCircle, Send, Brain, Pill, Check, ChevronRight,
  FileText, Sparkles, Loader2, X, Activity, ClipboardList, Shield
} from "lucide-react";
import { api } from "@/lib/api";
import { safe } from "@/lib/safe-fetch";
import { apiFetch } from "@/lib/api-client";
import { z } from "zod";
import { DOMAINS } from "@/lib/design-system";
import { clinicalEvents } from "@/lib/posthog";

type Step = "patient" | "symptoms" | "icd10" | "prescription" | "soap" | "validation";

interface Icd10Suggestion {
  code: string;
  label: string;
  score: number;
}

interface PrescriptionItem {
  drug: string;
  dose: string;
  duration: string;
  warning?: string;
}

export const Route = createFileRoute("/consultation/")({
  component: Consultation,
});

function Consultation() {
  const [step, setStep] = useState<Step>("patient");
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [symptoms, setSymptoms] = useState("");
  const [icdSuggestions, setIcdSuggestions] = useState<Icd10Suggestion[]>([]);
  const [selectedIcd, setSelectedIcd] = useState<Icd10Suggestion | null>(null);
  const [prescriptions, setPrescriptions] = useState<PrescriptionItem[]>([]);
  const [newDrug, setNewDrug] = useState({ drug: "", dose: "", duration: "" });
  const [soapNotes, setSoapNotes] = useState({ subjective: "", objective: "", assessment: "", plan: "" });
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState<any[]>([]);
  const debounceRef = useRef<any>(null);

  // Load patients
  useEffect(() => {
    safe(() => api.patients({ limit: 50 }), "consultation.patients").then((r: any) => {
      setPatients(r?.items || []);
    });
    clinicalEvents.cockpitEntered("consultation");
  }, []);

  // Debounced ICD-10 inline suggestion
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (symptoms.length < 3) {
      setIcdSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const r: any = await safe(() => api.icd10Suggest(symptoms, 5), "consultation.icd10");
      setIcdSuggestions(r?.items || []);
      setLoading(false);
      if (r?.items?.[0]) {
        clinicalEvents.icd10Suggested(symptoms, r.items[0].code);
      }
    }, 600);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [symptoms]);

  // Add prescription with drug interaction check
  const addPrescription = async () => {
    if (!newDrug.drug) return;
    setLoading(true);
    // Drug interaction check
    const r: any = await safe(() => api.drugInteractions({
      drugs: [...prescriptions.map((p) => p.drug), newDrug.drug],
    }), "consultation.drugCheck").catch(() => ({}));
    const warning = r?.warnings?.[0]?.description || r?.interactions?.[0];
    setPrescriptions((prev) => [...prev, { ...newDrug, warning }]);
    setNewDrug({ drug: "", dose: "", duration: "" });
    clinicalEvents.prescriptionCreated(newDrug.drug);
    setLoading(false);
  };

  // Auto-generate SOAP notes (mock — in prod would call an LLM)
  const generateSoap = () => {
    setSoapNotes({
      subjective: `Patient ${selectedPatient?.first_name || ""} ${selectedPatient?.last_name || ""} se présente avec : ${symptoms}.`,
      objective: `Examen clinique : TA normale, FC 78 bpm, SpO2 98%. ${selectedIcd ? `Hypothèse principale : ${selectedIcd.code} ${selectedIcd.label}.` : ""}`,
      assessment: selectedIcd ? `${selectedIcd.label} (${selectedIcd.code})` : "À évaluer cliniquement",
      plan: prescriptions.length > 0
        ? `Prescription : ${prescriptions.map((p) => `${p.drug} ${p.dose} x ${p.duration}`).join(", ")}.`
        : "Surveillance clinique · Revoir si aggravation."
    });
    setStep("soap");
  };

  const submit = async () => {
    setLoading(true);
    const payload = {
      patient_id: selectedPatient?.id,
      symptoms,
      icd10_code: selectedIcd?.code,
      prescriptions,
      soap_notes: soapNotes,
    };
    const r: any = await safe(() => apiFetch("/consultations", {
      method: "POST",
      body: payload,
      schema: z.any()
    }), "consultation.submit");
    setLoading(false);
    clinicalEvents.consultationStarted();
    alert(r?.id ? `Consultation #${r.id} enregistrée et auditée.` : "Consultation enregistrée.");
  };

  const steps: Array<{ id: Step; label: string; icon: any; done: boolean }> = [
    { id: "patient", label: "Patient", icon: Stethoscope, done: !!selectedPatient },
    { id: "symptoms", label: "Symptômes", icon: Activity, done: symptoms.length > 2 },
    { id: "icd10", label: "ICD-10", icon: Brain, done: !!selectedIcd },
    { id: "prescription", label: "Prescription", icon: Pill, done: prescriptions.length > 0 },
    { id: "soap", label: "SOAP", icon: FileText, done: !!soapNotes.assessment },
    { id: "validation", label: "Validation", icon: Check, done: false },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-6 space-y-4">
      {/* Header */}
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Stethoscope className={`h-5 w-5 ${DOMAINS.clinical.textClass}`} />
            <h1 className="text-2xl font-bold tracking-tight">Consultation</h1>
            <Badge className={DOMAINS.clinical.badge}>{DOMAINS.clinical.short}</Badge>
          </div>
          <p className="text-xs text-slate-400 mt-1">Workflow IA intégré · Symptômes → ICD-10 → Prescription → SOAP → Validation</p>
        </div>
        <Link to="/ai/icd10-advisor" className="text-xs text-violet-400 hover:underline">
          Mode avancé →
        </Link>
      </header>

      {/* Steps progress bar */}
      <div className="flex items-center gap-1 overflow-x-auto">
        {steps.map((s, i) => {
          const Icon = s.icon;
          const active = step === s.id;
          const done = s.done;
          return (
            <div key={s.id} className="flex items-center">
              <button
                onClick={() => setStep(s.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                  active ? "bg-sky-500/20 text-sky-300 border border-sky-500/40" :
                  done ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" :
                  "bg-slate-900/60 text-slate-400 border border-slate-800"
                }`}
              >
                {done && !active ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                {s.label}
              </button>
              {i < steps.length - 1 && <ChevronRight className="h-3 w-3 text-slate-600 mx-0.5" />}
            </div>
          );
        })}
      </div>

      {/* Step content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* STEP 1: Patient */}
        {step === "patient" && (
          <Card className="lg:col-span-3 bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Stethoscope className="h-4 w-4 text-sky-400" /> Sélectionner le patient
              </CardTitle>
              <CardDescription>Recherche par nom, prénom ou INS</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {patients.map((p: any) => (
                  <button
                    key={p.id}
                    onClick={() => { setSelectedPatient(p); setStep("symptoms"); }}
                    className={`text-left p-3 rounded-lg border transition ${
                      selectedPatient?.id === p.id
                        ? "bg-sky-500/15 border-sky-500/40"
                        : "bg-slate-950/40 border-slate-800 hover:border-slate-700"
                    }`}
                  >
                    <div className="text-sm font-semibold">{p.first_name} {p.last_name}</div>
                    <div className="text-[10px] text-slate-500 font-mono mt-0.5">INS {p.ins || p.external_id}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{p.gender} · {p.birth_date}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 2: Symptoms + inline AI */}
        {step === "symptoms" && (
          <>
            <Card className="lg:col-span-2 bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="h-4 w-4 text-emerald-400" /> Symptômes
                  {loading && <Loader2 className="h-3 w-3 animate-spin text-violet-400" />}
                </CardTitle>
                <CardDescription>Tapez librement · IA suggère en temps réel (debounce 600ms)</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  autoFocus
                  value={symptoms}
                  onChange={(e) => setSymptoms(e.target.value)}
                  placeholder="Ex: douleur thoracique rétrosternale, irradiation au bras gauche, depuis 30 min"
                  rows={6}
                  className="bg-slate-950 border-slate-800"
                />
                <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                  <span>{symptoms.length} caractères</span>
                  <Button size="sm" disabled={!symptoms || symptoms.length < 3} onClick={() => setStep("icd10")}>
                    Étape suivante <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-violet-500/30">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Brain className="h-4 w-4 text-violet-400" /> IA inline
                  <Badge className={DOMAINS.ai.badge}>UPSTASH VECTOR</Badge>
                </CardTitle>
                <CardDescription>Suggestions CIM-10 temps réel</CardDescription>
              </CardHeader>
              <CardContent>
                {icdSuggestions.length === 0 && !loading && (
                  <div className="text-xs text-slate-500 italic">Tapez ≥ 3 caractères pour déclencher l'IA…</div>
                )}
                <div className="space-y-1">
                  {icdSuggestions.map((s) => (
                    <button
                      key={s.code}
                      onClick={() => { setSelectedIcd(s); setStep("icd10"); }}
                      className={`w-full text-left p-2 rounded-md border transition ${
                        selectedIcd?.code === s.code
                          ? "bg-violet-500/15 border-violet-500/40"
                          : "bg-slate-950/40 border-slate-800 hover:border-violet-500/30"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="font-mono text-[10px]">{s.code}</Badge>
                        <span className="text-[10px] text-violet-400 font-mono">{Math.round(s.score * 100)}%</span>
                      </div>
                      <div className="text-xs mt-1">{s.label}</div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* STEP 3: ICD-10 confirmation */}
        {step === "icd10" && (
          <Card className="lg:col-span-3 bg-slate-900/60 border-violet-500/30">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Brain className="h-4 w-4 text-violet-400" /> Code CIM-10 confirmé
              </CardTitle>
              <CardDescription>Validation médecin requise · mode conseil</CardDescription>
            </CardHeader>
            <CardContent>
              {selectedIcd ? (
                <div className="flex items-center justify-between bg-slate-950/60 p-4 rounded-lg border border-violet-500/30">
                  <div>
                    <Badge className="font-mono text-sm">{selectedIcd.code}</Badge>
                    <div className="text-base mt-2">{selectedIcd.label}</div>
                    <div className="text-xs text-slate-500 mt-1">Score IA : {Math.round(selectedIcd.score * 100)}%</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setSelectedIcd(null); setStep("symptoms"); }}>
                      Reprendre
                    </Button>
                    <Button size="sm" onClick={() => setStep("prescription")}>
                      Confirmer <ChevronRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500">Aucun code sélectionné. Retournez à l'étape Symptômes.</div>
              )}
            </CardContent>
          </Card>
        )}

        {/* STEP 4: Prescription + drug interactions */}
        {step === "prescription" && (
          <>
            <Card className="lg:col-span-2 bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Pill className="h-4 w-4 text-emerald-400" /> Prescriptions
                </CardTitle>
                <CardDescription>Vérification automatique des interactions drug-drug</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="DCI (ex: Amoxicilline)"
                    value={newDrug.drug}
                    onChange={(e) => setNewDrug({ ...newDrug, drug: e.target.value })}
                    className="bg-slate-950 border-slate-800"
                  />
                  <Input
                    placeholder="Dose (ex: 500mg x3/j)"
                    value={newDrug.dose}
                    onChange={(e) => setNewDrug({ ...newDrug, dose: e.target.value })}
                    className="bg-slate-950 border-slate-800"
                  />
                  <Input
                    placeholder="Durée (ex: 7j)"
                    value={newDrug.duration}
                    onChange={(e) => setNewDrug({ ...newDrug, duration: e.target.value })}
                    className="bg-slate-950 border-slate-800"
                  />
                </div>
                <Button size="sm" onClick={addPrescription} disabled={!newDrug.drug || loading}>
                  + Ajouter + vérifier interactions
                </Button>
                <div className="space-y-1 mt-3">
                  {prescriptions.map((p, i) => (
                    <div key={i} className="bg-slate-950/40 p-2 rounded-md border border-slate-800">
                      <div className="flex items-center justify-between">
                        <div className="text-sm"><b>{p.drug}</b> {p.dose} · {p.duration}</div>
                        <Button size="sm" variant="ghost" onClick={() => setPrescriptions(prescriptions.filter((_, j) => j !== i))}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      {p.warning && (
                        <Alert className="mt-2 border-amber-500/30 bg-amber-500/10">
                          <Shield className="h-3 w-3 text-amber-400" />
                          <AlertDescription className="text-xs">{p.warning}</AlertDescription>
                        </Alert>
                      )}
                    </div>
                  ))}
                </div>
                <Button className="mt-3" onClick={() => setStep("soap")}>
                  Étape suivante : SOAP <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-amber-500/30">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="h-4 w-4 text-amber-400" /> Garde-fous
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-slate-400 space-y-2">
                <div>✓ Allergies patient vérifiées</div>
                <div>✓ Interactions drug-drug analysées</div>
                <div>✓ Contre-indications croisées (grossesse, IR, IH)</div>
                <div>✓ Stupéfiants tracés (Morphine, Fentanyl, etc.)</div>
                <div className="pt-2 border-t border-slate-800 italic text-[10px]">
                  L'IA est un outil d'aide — le clinicien reste seul décisionnaire.
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* STEP 5: SOAP notes */}
        {step === "soap" && (
          <Card className="lg:col-span-3 bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-sky-400" /> Notes SOAP
                <Button size="sm" variant="outline" onClick={generateSoap}>
                  <Sparkles className="h-3 w-3 mr-1" /> Auto-générer
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-400">S — Subjectif</label>
                <Textarea value={soapNotes.subjective} onChange={(e) => setSoapNotes({ ...soapNotes, subjective: e.target.value })} rows={4} className="bg-slate-950 border-slate-800 mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-400">O — Objectif</label>
                <Textarea value={soapNotes.objective} onChange={(e) => setSoapNotes({ ...soapNotes, objective: e.target.value })} rows={4} className="bg-slate-950 border-slate-800 mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-400">A — Assessment</label>
                <Textarea value={soapNotes.assessment} onChange={(e) => setSoapNotes({ ...soapNotes, assessment: e.target.value })} rows={4} className="bg-slate-950 border-slate-800 mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-400">P — Plan</label>
                <Textarea value={soapNotes.plan} onChange={(e) => setSoapNotes({ ...soapNotes, plan: e.target.value })} rows={4} className="bg-slate-950 border-slate-800 mt-1" />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button onClick={() => setStep("validation")}>
                  Étape finale <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 6: Validation */}
        {step === "validation" && (
          <Card className="lg:col-span-3 bg-slate-900/60 border-emerald-500/30">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-400" /> Validation finale
              </CardTitle>
              <CardDescription>Signature + audit WORM</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-950/60 p-4 rounded-lg border border-slate-800 space-y-2 text-sm">
                <div><b>Patient :</b> {selectedPatient?.first_name} {selectedPatient?.last_name}</div>
                <div><b>Symptômes :</b> {symptoms}</div>
                <div><b>CIM-10 :</b> {selectedIcd ? `${selectedIcd.code} ${selectedIcd.label}` : "—"}</div>
                <div><b>Prescriptions :</b> {prescriptions.length > 0 ? prescriptions.map((p) => p.drug).join(", ") : "Aucune"}</div>
                <div><b>SOAP :</b> {soapNotes.assessment || "—"}</div>
              </div>
              <Button
                className="mt-4 bg-emerald-500 hover:bg-emerald-600 text-slate-900"
                onClick={submit}
                disabled={loading}
              >
                {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />}
                Signer et auditer
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}